/**
 * Shopify Attributes Extension for Chekly CheckoutManager
 * 
 * Extracts Shopify attributes from URL parameters (e.g., attributes[Referral]=Useik)
 * and passes them to the redirect URL as additional parameters.
 * Attributes are accumulated in a cookie for persistence across page navigations.
 */

(function () {
  'use strict';

  const COOKIE_NAME = 'chekly_shopify_attributes';
  const COOKIE_EXPIRATION_DAYS = 7;

  /**
   * Get cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|undefined} Cookie value or undefined
   */
  function getCookie(name) {
    const escapedName = name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1');
    const regex = new RegExp(`(?:^|; )${escapedName}=([^;]*)`);
    const match = document.cookie.match(regex);
    return match ? decodeURIComponent(match[1]) : undefined;
  }

  /**
   * Set cookie with custom expiration
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Expiration in days
   */
  function setCookie(name, value, days) {
    const expiration = new Date(Date.now() + 24 * 60 * 60 * 1000 * days).toUTCString();
    document.cookie = `${name}=${value}; expires=${expiration}; path=/;`;
  }

  /**
   * Extract Shopify attributes from URL parameters
   * Handles both "attributes" and "attibutes" (typo variant)
   * @param {URLSearchParams} urlParams - URL search parameters
   * @returns {Object} Object containing extracted attributes
   */
  function extractShopifyAttributes(urlParams) {
    const attributes = {};
    
    urlParams.forEach((value, key) => {
      const attributeMatch = key.match(/^attrib?utes\[(.+)\]$/i);
      if (attributeMatch) {
        const attributeKey = attributeMatch[1];
        attributes[attributeKey] = value;
      }
    });
    
    return attributes;
  }

  /**
   * Load attributes from cookie
   * @returns {Object} Attributes object from cookie
   */
  function loadAttributesFromCookie() {
    try {
      const cookieValue = getCookie(COOKIE_NAME);
      if (cookieValue) {
        return JSON.parse(cookieValue);
      }
    } catch (error) {
    }
    return {};
  }

  /**
   * Save attributes to cookie
   * @param {Object} attributes - Attributes object to save
   */
  function saveAttributesToCookie(attributes) {
    try {
      const jsonString = JSON.stringify(attributes);
      setCookie(COOKIE_NAME, jsonString, COOKIE_EXPIRATION_DAYS);
    } catch (error) {
    }
  }

  /**
   * Merge attributes from URL and cookie
   * URL attributes take precedence over cookie attributes
   * @param {Object} urlAttributes - Attributes from URL
   * @param {Object} cookieAttributes - Attributes from cookie
   * @returns {Object} Merged attributes object
   */
  function mergeAttributes(urlAttributes, cookieAttributes) {
    const merged = { ...cookieAttributes };
    for (const key in urlAttributes) {
      if (urlAttributes.hasOwnProperty(key)) {
        merged[key] = urlAttributes[key];
      }
    }
    return merged;
  }

  /**
   * Build query string from attributes object
   * @param {Object} attributes - Attributes object
   * @returns {string} Query string with attributes
   */
  function buildAttributesQueryString(attributes) {
    const params = [];
    
    for (const key in attributes) {
      if (attributes.hasOwnProperty(key)) {
        const encodedKey = encodeURIComponent(`attributes[${key}]`);
        const encodedValue = encodeURIComponent(attributes[key]);
        params.push(`${encodedKey}=${encodedValue}`);
      }
    }
    
    return params.join('&');
  }

  /**
   * Get all accumulated attributes (from URL and cookie)
   * @returns {Object} Merged attributes object
   */
  function getAllAttributes() {
    const currentUrlParams = new URLSearchParams(window.location.search);
    const urlAttributes = extractShopifyAttributes(currentUrlParams);
    const cookieAttributes = loadAttributesFromCookie();
    const allAttributes = mergeAttributes(urlAttributes, cookieAttributes);
    
    if (Object.keys(allAttributes).length > 0) {
      saveAttributesToCookie(allAttributes);
    }
    
    return allAttributes;
  }

  /**
   * Setup Shopify attributes extension
   */
  function setupShopifyAttributesExtension() {
    if (typeof window === 'undefined' || !window.checkoutManager) {
      setTimeout(setupShopifyAttributesExtension, 50);
      return;
    }

    const manager = window.checkoutManager;
    
    getAllAttributes();

    manager.on('beforeTrackingSave', (urlParams) => {
      const urlAttributes = extractShopifyAttributes(urlParams);
      if (Object.keys(urlAttributes).length > 0) {
        const cookieAttributes = loadAttributesFromCookie();
        const allAttributes = mergeAttributes(urlAttributes, cookieAttributes);
        saveAttributesToCookie(allAttributes);
      }
    });

    manager.on('afterUrlBuild', (url) => {
      const allAttributes = getAllAttributes();
      
      if (Object.keys(allAttributes).length === 0) {
        return url;
      }
      
      const attributesQueryString = buildAttributesQueryString(allAttributes);
      
      if (!attributesQueryString) {
        return url;
      }
      
      if (url.indexOf(attributesQueryString) !== -1) {
        return url;
      }
      
      const hasQuery = url.indexOf('?') !== -1;
      const separator = hasQuery ? '&' : '?';
      const modifiedUrl = url + separator + attributesQueryString;
      
      return modifiedUrl;
    });
  }

  getAllAttributes();

  function trySetup() {
    if (typeof window !== 'undefined' && window.checkoutManager) {
      setupShopifyAttributesExtension();
      return true;
    }
    return false;
  }

  if (!trySetup()) {
    const checkInterval = setInterval(() => {
      if (trySetup()) {
        clearInterval(checkInterval);
      }
    }, 10);
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        trySetup();
        clearInterval(checkInterval);
      });
    }
    
    window.addEventListener('load', () => {
      trySetup();
      clearInterval(checkInterval);
    });
    
    setTimeout(() => {
      clearInterval(checkInterval);
      trySetup();
    }, 2000);
  }
})();
